import { getTranslation } from 'helpers/getTranslation';

import {
  eventNameToTranslationKey,
  getAnalyticsEventBaseName,
} from './analyticsEventUtils';
import {
  resolveAuthMethodFromProperties,
  translateAuthMethod,
} from './analyticsPropertyLabels';
import { resolveCatalogParentLabel } from './catalogContextDisplay';
import {
  formatAnalyticsEventDisplay,
  isKnownAnalyticsEvent,
  translateAnalyticsEvent,
} from './formatAnalyticsEventLabel';
import {
  describeClientErrorBriefly,
  describeEndpoint,
  getApiErrorTitle,
  getClientErrorKindLabel,
  getEndpointWhen,
  getHttpStatusKind,
  getHttpStatusLabel,
} from './journeyEndpointLabels';
import { resolveModuleFlowKind } from './moduleTypeIcons';

/** Backend-only events shown in client journeys — icon instead of “(server)” in labels. */
export const SERVER_EVENT_BADGE_ICON = 'structure';
export const SERVER_EVENT_BADGE_ICON_SIZE = 9;

export const SERVER_JOURNEY_EVENT_NAMES = new Set([
  'Task Completed',
  'Activation Completed',
  'Onboarding Survey Answered',
  'Signup Completed',
  'Payment Succeeded',
  'Payment Failed',
  'Subscription Renewed',
  'Subscription Cancelled',
]);

export const isServerJourneyEvent = (params: {
  eventName?: string;
  platform?: string;
  properties?: Record<string, unknown>;
}): boolean => {
  if (params.platform === 'Backend' || params.properties?.source === 'server') {
    return true;
  }

  if (!params.eventName) {
    return false;
  }

  return SERVER_JOURNEY_EVENT_NAMES.has(
    getAnalyticsEventBaseName(params.eventName),
  );
};

/* Contract types — mirror admin-api `AnalyticsStudentSessionsDto` / `AnalyticsStudentSessionDetailDto`. */

export type ExitCause = 'error' | 'stuck' | 'idle' | 'goal' | 'left';
export type ClientPlatform = 'Web' | 'Plugin';
export type StripCell = 'normal' | 'error' | 'slow' | 'goal' | 'gap';
export type SessionEndReason = 'closed' | 'idle_timeout' | 'logout' | 'restart';
export type EventKind =
  | 'view'
  | 'action'
  | 'server'
  | 'goal'
  | 'error'
  | 'warning'
  | 'slow'
  | 'rage'
  | 'exit';

export type SessionEnv = {
  appVersion: string | null;
  browser: string | null;
  browserVersion: string | null;
  os: string | null;
  deviceType: string | null;
  viewport: string | null;
  language: string | null;
  timezone: string | null;
  figmaEditorType: string | null;
  figmaApiVersion: string | null;
};

export type SessionScreenRef = {
  eventName: string;
  properties: Record<string, unknown>;
};

export type SessionSummary = {
  id: string;
  sessionId: string | null;
  platform: ClientPlatform;
  startedAt: string;
  endedAt: string;
  durationMs: number;
  eventsCount: number;
  actionsCount: number;
  screensCount: number;
  exitCause: ExitCause;
  exitScreen: SessionScreenRef | null;
  endReason: SessionEndReason | null;
  errorsCount: number;
  slowCount: number;
  hasProblems: boolean;
  followedByReload: boolean;
  strip: StripCell[];
  env: SessionEnv | null;
  /**
   * Optional (newer admin-api): the event behind an `error` / `stuck` exit — the
   * root cause when one was found — with its diagnostic properties (method,
   * endpoint, status, error_code, error_kind, fatal, thread, message_type,
   * stage, completed, target). Without it the exit screens name only the cause.
   */
  exitProblem?: SessionScreenRef | null;
};

/** One kind of problem behind the exits from a screen: «ошибка при загрузке урока · 2 раза». */
export type ExitProblemStat = {
  key: string;
  cause: Extract<ExitCause, 'error' | 'stuck'>;
  count: number;
  /** A representative event of the problem (the latest one). */
  event: SessionScreenRef;
};

export type ExitScreenStat = SessionScreenRef & {
  total: number;
  byCause: Record<ExitCause, number>;
  /** Problems behind the error / stuck exits, most frequent first (needs `exitProblem`). */
  problems?: ExitProblemStat[];
};

export type AnalyticsStudentSessionsDto = {
  sessions: SessionSummary[];
  platformCounts: { all: number; Web: number; Plugin: number };
  summary: {
    totalSessions: number;
    lastSeenAt: string | null;
    lastPlatform: ClientPlatform | null;
    medianDurationMs: number;
    exitCauseCounts: Record<ExitCause, number>;
    exitScreens: ExitScreenStat[];
  };
  truncated: boolean;
};

export type SessionServerError = {
  message: string | null;
  errorName: string | null;
  stack: string | null;
  endpoint: string | null;
  status: number | null;
};

export type SessionEvent = {
  index: number;
  eventName: string;
  timestamp: string;
  offsetMs: number;
  gapBeforeMs: number;
  kind: EventKind;
  source: 'client' | 'server';
  platform: 'Web' | 'Plugin' | 'Backend';
  screenIndex: number;
  properties: Record<string, unknown>;
  serverError?: SessionServerError;
};

export type SessionScreen = {
  index: number;
  eventName: string | null;
  properties: Record<string, unknown>;
  startedAt: string;
  durationMs: number;
  firstEventIndex: number;
  lastEventIndex: number;
  hasError: boolean;
  hasSlow: boolean;
  hasGoal: boolean;
};

export type SessionExit = {
  cause: ExitCause;
  causeEventIndex: number | null;
  sinceCauseMs: number | null;
  screenIndex: number | null;
};

export type AnalyticsStudentSessionDetailDto = SessionSummary & {
  exit: SessionExit;
  screens: SessionScreen[];
  events: SessionEvent[];
};

/* UI constants */

export const PLATFORM_FILTER_ALL = 'all';

export type PlatformFilter = typeof PLATFORM_FILTER_ALL | ClientPlatform;

export const SESSIONS_LOOKBACK_DAYS = 90;
export const SESSIONS_LIMIT = 100;
export const EXIT_SCREENS_LIMIT = 8;
/** Order of causes in legends and stacked bars (problems first). */
export const EXIT_CAUSES: ExitCause[] = [
  'error',
  'stuck',
  'idle',
  'left',
  'goal',
];
export const LOG_GAP_THRESHOLD_MS = 2 * 60 * 1000;
export const JOURNEY_HIGHLIGHT_DURATION_MS = 1600;
/** Detail is fetched once the selection settles (holding J/K skips the sessions in between). */
export const SESSION_DETAIL_FETCH_DELAY_MS = 200;
export const SESSION_DETAIL_ELEMENT_ID = 'journey-session-detail';

const MS_IN_SECOND = 1000;
const SECONDS_IN_MINUTE = 60;
const MINUTES_IN_HOUR = 60;
const MS_IN_DAY = 24 * 60 * 60 * MS_IN_SECOND;
const DEFAULT_SLOW_REQUEST_THRESHOLD_MS = 3000;
const DEFAULT_SLOW_SCREEN_THRESHOLD_MS = 8000;
const SLOW_SCALE_HEADROOM = 1.25;
const MIN_SCREEN_SEGMENT_SHARE = 0.04;
/** Narrower flow-bar segments show «Урок» / «Тренажёр» instead of a cut-off name. */
const SHORT_SCREEN_SEGMENT_SHARE = 0.2;
/*
 * Flow-bar geometry (ScreenFlowBar.module.scss: `.segmentSlot` min-width,
 * `.segment` padding) and an average body3 glyph. A narrow segment widens to
 * fit its short word («Приветствие», «Тренажёр») up to the cap; a word longer
 * than that is not printed at all (the duration stays, the name is in the
 * tooltip) — the bar never shows «Арк…» / «Стр…».
 */
const FLOW_SEGMENT_MIN_WIDTH_PX = 56;
const FLOW_SEGMENT_LABEL_MAX_WIDTH_PX = 104;
const FLOW_SEGMENT_PADDING_PX = 16;
const FLOW_LABEL_CHAR_WIDTH_PX = 8;
/** Catalog names in verdict titles are cut to about this length (full name in the log). */
export const SCREEN_NAME_MAX_LENGTH = 48;
/** A desktop web window narrower than this is flagged as «маленькое окно». */
const SMALL_WEB_VIEWPORT_WIDTH = 1024;

export const EXIT_CAUSE_TAG_VARIANTS: Record<ExitCause, string> = {
  error: 'error',
  stuck: 'warning',
  goal: 'success',
  left: 'secondary',
  idle: 'neutral',
};

export const EXIT_CAUSE_ICONS: Record<ExitCause, string> = {
  error: 'warning',
  stuck: 'time',
  goal: 'checkRound',
  left: 'logout',
  idle: 'pause',
};

/** Step glyphs from the approved journey prototype (src/assets/icons/journey-*.svg). */
export const EVENT_KIND_ICONS: Record<EventKind, string> = {
  view: 'journeyView',
  action: 'journeyClick',
  server: 'journeyServer',
  goal: 'journeyGoal',
  error: 'journeyError',
  warning: 'journeyError',
  slow: 'journeySlow',
  rage: 'journeyRage',
  exit: 'journeyExit',
};

export const IDLE_EXIT_EVENT_ICON = 'journeyIdle';

export type JourneyHighlight = {
  target: 'event' | 'screen';
  index: number;
};

export const getJourneyEventElementId = (eventIndex: number) =>
  `journey-event-${eventIndex}`;

export const getJourneyScreenElementId = (screenIndex: number) =>
  `journey-screen-${screenIndex}`;

export const getJourneyHighlightElementId = (highlight: JourneyHighlight) =>
  highlight.target === 'event'
    ? getJourneyEventElementId(highlight.index)
    : getJourneyScreenElementId(highlight.index);

/* Value readers */

const readNonEmptyString = (value: unknown): string | null =>
  typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;

const readFiniteNumber = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

const joinNonEmpty = (
  parts: Array<string | null | undefined>,
  separator = ' · ',
) =>
  parts
    .filter((part): part is string => Boolean(part && part.length > 0))
    .join(separator);

/** `null` when the key has no translation (i18next returns the key itself). */
const translateOptional = (
  key: string,
  params?: Record<string, unknown>,
): string | null => {
  const translated = getTranslation(key, params);

  return translated === key ? null : translated;
};

const capitalizeFirst = (value: string) =>
  value ? `${value.charAt(0).toUpperCase()}${value.slice(1)}` : value;

/** Names that keep their capital letter inside a sentence. */
const PROPER_NAME_STARTS = new Set([
  'Figma',
  'FigJam',
  'Google',
  'Chrome',
  'Safari',
  'Firefox',
  'Yandex',
  'Edge',
  'Opera',
  'Pro',
]);

/**
 * «Урок «…»» → «урок «…»» after a colon or a comma inside a sentence. Only a
 * capitalised ordinary word is lowered: brand names (Figma, Google) and
 * abbreviations (UI, API) stay as they are.
 */
const lowercaseFirst = (value: string) => {
  const firstWord = /^[A-Za-zА-Яа-яЁё]+/.exec(value)?.[0] ?? '';

  return /^[A-ZА-ЯЁ][a-zа-яё]*$/.test(firstWord) &&
    !PROPER_NAME_STARTS.has(firstWord)
    ? `${value.charAt(0).toLowerCase()}${value.slice(1)}`
    : value;
};

const truncateText = (value: string, maxLength?: number) =>
  maxLength == null || value.length <= maxLength
    ? value
    : `${value.slice(0, Math.max(maxLength - 1, 1)).trimEnd()}…`;

/* Response normalisation */

const createEmptyCauseCounts = (): Record<ExitCause, number> => ({
  error: 0,
  stuck: 0,
  idle: 0,
  goal: 0,
  left: 0,
});

export const EMPTY_SESSIONS_RESPONSE: AnalyticsStudentSessionsDto = {
  sessions: [],
  platformCounts: { all: 0, Web: 0, Plugin: 0 },
  summary: {
    totalSessions: 0,
    lastSeenAt: null,
    lastPlatform: null,
    medianDurationMs: 0,
    exitCauseCounts: createEmptyCauseCounts(),
    exitScreens: [],
  },
  truncated: false,
};

export const normalizeSessionsResponse = (
  response: Partial<AnalyticsStudentSessionsDto> | null | undefined,
): AnalyticsStudentSessionsDto => {
  const sessions = (response?.sessions ?? []).map((session) => ({
    ...session,
    strip: session.strip ?? [],
  }));

  return {
    sessions,
    platformCounts: response?.platformCounts ?? {
      all: sessions.length,
      Web: sessions.filter((session) => session.platform === 'Web').length,
      Plugin: sessions.filter((session) => session.platform === 'Plugin')
        .length,
    },
    summary: response?.summary ?? EMPTY_SESSIONS_RESPONSE.summary,
    truncated: Boolean(response?.truncated),
  };
};

export const normalizeSessionDetail = (
  response: AnalyticsStudentSessionDetailDto,
): AnalyticsStudentSessionDetailDto => ({
  ...response,
  strip: response.strip ?? [],
  screens: response.screens ?? [],
  events: response.events ?? [],
  exit: response.exit ?? {
    cause: response.exitCause,
    causeEventIndex: null,
    sinceCauseMs: null,
    screenIndex: null,
  },
});

export const getSessionsDateRange = (now: Date) => ({
  from: new Date(
    now.getTime() - SESSIONS_LOOKBACK_DAYS * MS_IN_DAY,
  ).toISOString(),
  to: now.toISOString(),
});

/* Filters and summary */

export const filterSessionsByPlatform = (
  sessions: SessionSummary[],
  platform: PlatformFilter,
) =>
  platform === PLATFORM_FILTER_ALL
    ? sessions
    : sessions.filter((session) => session.platform === platform);

export const filterVisibleSessions = (
  sessions: SessionSummary[],
  platform: PlatformFilter,
  onlyProblems: boolean,
) =>
  filterSessionsByPlatform(sessions, platform).filter(
    (session) => !onlyProblems || session.hasProblems,
  );

export const getPlatformTabs = (
  platformCounts: AnalyticsStudentSessionsDto['platformCounts'],
) => [
  {
    value: PLATFORM_FILTER_ALL,
    label: getTranslation('student_page.journey.platform_all'),
    count: platformCounts.all,
  },
  {
    value: 'Web',
    label: getTranslation('common.text.web'),
    icon: 'browser',
    count: platformCounts.Web,
  },
  {
    value: 'Plugin',
    label: getTranslation('common.text.plugin'),
    icon: 'figma',
    count: platformCounts.Plugin,
  },
];

export const getMedian = (values: number[]) => {
  if (values.length === 0) {
    return 0;
  }

  const sortedValues = [...values].sort((left, right) => left - right);
  const middleIndex = Math.floor(sortedValues.length / 2);

  return sortedValues.length % 2 === 0
    ? Math.round(
        (sortedValues[middleIndex - 1] + sortedValues[middleIndex]) / 2,
      )
    : sortedValues[middleIndex];
};

const getTimestamp = (iso: string) => {
  const time = new Date(iso).getTime();

  return Number.isNaN(time) ? 0 : time;
};

const sortNewestFirst = (sessions: SessionSummary[]) =>
  [...sessions].sort(
    (left, right) =>
      getTimestamp(right.startedAt) - getTimestamp(left.startedAt),
  );

/** Catalog id of a property as a string (ids may come as numbers), else `null`. */
const readCatalogId = (value: unknown): string | null =>
  typeof value === 'number' && Number.isFinite(value)
    ? String(value)
    : readNonEmptyString(value);

/** Trainer / training / arcade / course the event belongs to — one identity for all flows. */
const readModuleIdentity = (
  properties: Record<string, unknown> | undefined,
): string | null =>
  readCatalogId(properties?.module_id) ??
  readCatalogId(properties?.training_id) ??
  readCatalogId(properties?.arcade_id) ??
  readCatalogId(properties?.course_id);

/**
 * Exit screens are grouped by event + lesson + module ids, so two unnamed
 * lessons of one trainer stay apart; names are the fallback for payloads without ids.
 */
export const getExitScreenKey = (screen: SessionScreenRef) =>
  [
    screen.eventName,
    readCatalogId(screen.properties?.lesson_id) ??
      readNonEmptyString(screen.properties?.lesson_name) ??
      '',
    readModuleIdentity(screen.properties) ??
      readNonEmptyString(screen.properties?.module_name) ??
      '',
  ].join('\u0000');

/**
 * Same problem = same failed request (route dictionary key), same crash kind,
 * same slow stage or request, rage clicks, or the same failed step (Login Failed…).
 */
export const getExitProblemKey = (event: SessionScreenRef): string => {
  const properties = event.properties ?? {};
  const baseName = getAnalyticsEventBaseName(event.eventName);
  const endpointKey = () =>
    describeEndpoint(
      readNonEmptyString(properties.method),
      readNonEmptyString(properties.endpoint),
    ).key;

  switch (baseName) {
    case 'API Error':
      return `api:${endpointKey()}`;
    case 'Slow Request':
      return `slow_request:${endpointKey()}`;
    case 'Client Error':
      return `crash:${getClientErrorKindLabel(properties)}`;
    case 'Slow Screen':
      return `slow_screen:${readNonEmptyString(properties.stage) ?? ''}:${
        properties.completed === false ? 'not_completed' : ''
      }`;
    case 'Rage Click':
      return 'rage';
    default:
      return `event:${baseName}`;
  }
};

const EXIT_PROBLEM_CAUSES = new Set<ExitCause>(['error', 'stuck']);

const addExitProblem = (stat: ExitScreenStat, session: SessionSummary) => {
  if (
    !session.exitProblem?.eventName ||
    !EXIT_PROBLEM_CAUSES.has(session.exitCause)
  ) {
    return;
  }

  const cause = session.exitCause as ExitProblemStat['cause'];
  const key = `${cause}\u0000${getExitProblemKey(session.exitProblem)}`;
  const problems = stat.problems ?? [];
  const problem = problems.find((candidate) => candidate.key === key);

  if (problem) {
    problem.count += 1;
  } else {
    problems.push({ key, cause, count: 1, event: session.exitProblem });
  }

  stat.problems = problems;
};

/** Error first on a tie: it is the one to fix. */
const compareExitProblems = (left: ExitProblemStat, right: ExitProblemStat) =>
  right.count - left.count ||
  (left.cause === right.cause ? 0 : left.cause === 'error' ? -1 : 1);

export const buildExitScreenStats = (
  sessions: SessionSummary[],
  limit = EXIT_SCREENS_LIMIT,
): ExitScreenStat[] => {
  const statsByKey = new Map<string, ExitScreenStat>();

  sortNewestFirst(sessions).forEach((session) => {
    if (!session.exitScreen) {
      return;
    }

    const key = getExitScreenKey(session.exitScreen);
    const stat = statsByKey.get(key) ?? {
      eventName: session.exitScreen.eventName,
      properties: session.exitScreen.properties ?? {},
      total: 0,
      byCause: createEmptyCauseCounts(),
    };

    stat.total += 1;
    stat.byCause[session.exitCause] += 1;
    addExitProblem(stat, session);
    statsByKey.set(key, stat);
  });

  return [...statsByKey.values()]
    .map((stat) =>
      stat.problems
        ? { ...stat, problems: [...stat.problems].sort(compareExitProblems) }
        : stat,
    )
    .sort((left, right) => right.total - left.total)
    .slice(0, limit);
};

export type JourneySummaryStats = {
  totalSessions: number;
  lastSession: SessionSummary | null;
  medianDurationMs: number;
  goalCount: number;
  goalPercent: number;
  errorCount: number;
  stuckCount: number;
  latestErrorSessionId: string | null;
  latestStuckSessionId: string | null;
  topExitScreen: ExitScreenStat | null;
};

/** Recomputes the summary strip for the sessions of the active platform filter. */
export const buildJourneySummary = (
  sessions: SessionSummary[],
): JourneySummaryStats => {
  const newestFirst = sortNewestFirst(sessions);
  const goalCount = sessions.filter(
    (session) => session.exitCause === 'goal',
  ).length;
  const errorSessions = newestFirst.filter(
    (session) => session.exitCause === 'error',
  );
  const stuckSessions = newestFirst.filter(
    (session) => session.exitCause === 'stuck',
  );

  return {
    totalSessions: sessions.length,
    lastSession: newestFirst[0] ?? null,
    medianDurationMs: getMedian(sessions.map((session) => session.durationMs)),
    goalCount,
    goalPercent:
      sessions.length > 0 ? Math.round((goalCount / sessions.length) * 100) : 0,
    errorCount: errorSessions.length,
    stuckCount: stuckSessions.length,
    latestErrorSessionId: errorSessions[0]?.id ?? null,
    latestStuckSessionId: stuckSessions[0]?.id ?? null,
    topExitScreen: buildExitScreenStats(sessions, 1)[0] ?? null,
  };
};

/* Formatting */

export const formatDuration = (durationMs: number): string => {
  const totalSeconds = Math.max(
    0,
    Math.round((durationMs || 0) / MS_IN_SECOND),
  );

  if (totalSeconds < SECONDS_IN_MINUTE) {
    return getTranslation('student_page.journey.duration.seconds', {
      seconds: totalSeconds,
    });
  }

  const totalMinutes = Math.floor(totalSeconds / SECONDS_IN_MINUTE);
  const seconds = totalSeconds % SECONDS_IN_MINUTE;

  if (totalMinutes < MINUTES_IN_HOUR) {
    return seconds > 0
      ? getTranslation('student_page.journey.duration.minutes_seconds', {
          minutes: totalMinutes,
          seconds,
        })
      : getTranslation('student_page.journey.duration.minutes', {
          minutes: totalMinutes,
        });
  }

  const hours = Math.floor(totalMinutes / MINUTES_IN_HOUR);
  const minutes = totalMinutes % MINUTES_IN_HOUR;

  return minutes > 0
    ? getTranslation('student_page.journey.duration.hours_minutes', {
        hours,
        minutes,
      })
    : getTranslation('student_page.journey.duration.hours', { hours });
};

/** Seconds with one decimal (localized separator) — for request/screen timings. */
export const formatPreciseSeconds = (durationMs: number): string =>
  getTranslation('student_page.journey.duration.seconds_precise', {
    value: Math.round(Math.max(0, durationMs || 0) / 100) / 10,
  });

const padTwoDigits = (value: number) => String(value).padStart(2, '0');

/** `m:ss` (or `h:mm:ss`) from the session start. */
export const formatClock = (durationMs: number): string => {
  const totalSeconds = Math.max(
    0,
    Math.floor((durationMs || 0) / MS_IN_SECOND),
  );
  const hours = Math.floor(
    totalSeconds / (SECONDS_IN_MINUTE * MINUTES_IN_HOUR),
  );
  const minutes =
    Math.floor(totalSeconds / SECONDS_IN_MINUTE) % MINUTES_IN_HOUR;
  const seconds = totalSeconds % SECONDS_IN_MINUTE;

  return hours > 0
    ? `${hours}:${padTwoDigits(minutes)}:${padTwoDigits(seconds)}`
    : `${minutes}:${padTwoDigits(seconds)}`;
};

export const formatOffset = (durationMs: number): string =>
  `+${formatClock(durationMs)}`;

export const formatSessionTime = (iso: string, locale: string) => {
  const date = new Date(iso);

  if (Number.isNaN(date.getTime())) {
    return '—';
  }

  return new Intl.DateTimeFormat(locale, {
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

export const formatSessionDay = (
  iso: string,
  locale: string,
  now: Date = new Date(),
) => {
  const date = new Date(iso);

  if (Number.isNaN(date.getTime())) {
    return '—';
  }

  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'long',
    ...(date.getFullYear() !== now.getFullYear()
      ? { year: 'numeric' as const }
      : {}),
  }).format(date);
};

const getDateDayKey = (date: Date) =>
  `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;

const getLocalDayKey = (iso: string) => getDateDayKey(new Date(iso));

/** «сегодня» / «вчера» for the last two local days, otherwise «25 сентября». */
export const formatRelativeDay = (
  iso: string,
  locale: string,
  now: Date = new Date(),
): string => {
  const date = new Date(iso);

  if (Number.isNaN(date.getTime())) {
    return '—';
  }

  const yesterday = new Date(now);

  yesterday.setDate(now.getDate() - 1);

  if (getDateDayKey(date) === getDateDayKey(now)) {
    return getTranslation('student_page.journey.relative_days.today');
  }

  if (getDateDayKey(date) === getDateDayKey(yesterday)) {
    return getTranslation('student_page.journey.relative_days.yesterday');
  }

  return formatSessionDay(iso, locale, now);
};

/** Same as `formatRelativeDay`, capitalised for headers: «Сегодня», «Вчера». */
export const formatRelativeDayTitle = (
  iso: string,
  locale: string,
  now: Date = new Date(),
) => capitalizeFirst(formatRelativeDay(iso, locale, now));

export type SessionDayGroup = {
  dayKey: string;
  label: string;
  sessions: SessionSummary[];
};

/** Groups sessions (kept in the given order, newest first) under local-day headers. */
export const groupSessionsByDay = (
  sessions: SessionSummary[],
  locale: string,
  now: Date = new Date(),
): SessionDayGroup[] =>
  sessions.reduce<SessionDayGroup[]>((groups, session) => {
    const dayKey = getLocalDayKey(session.startedAt);
    const lastGroup = groups[groups.length - 1];

    if (lastGroup && lastGroup.dayKey === dayKey) {
      lastGroup.sessions.push(session);
      return groups;
    }

    return [
      ...groups,
      {
        dayKey,
        label: formatRelativeDayTitle(session.startedAt, locale, now),
        sessions: [session],
      },
    ];
  }, []);

/* Labels */

export const getExitCauseLabel = (cause: ExitCause) =>
  getTranslation(`student_page.journey.exit_causes.${cause}`);

const SCREENS_PREFIX = 'student_page.journey.screens';

/**
 * `App Opened` → the screen of its `entry_path` (web route patterns from
 * befront-web-ui `screenViews.ts`; raw paths are never shown).
 */
const ENTRY_PATH_SCREEN_EVENTS: ReadonlyArray<[RegExp, string]> = [
  [/^\/auth\/?$/, 'Auth Viewed'],
  [/^\/oauth(?:\/plugin\/redirect)?\/?$/, 'OAuth Viewed'],
  [/^\/profile-setup\/?$/, 'Profile Setup Viewed'],
  [/^\/onboarding\/?$/, 'Onboarding Viewed'],
  [/^\/learn\/?$/, 'Learn Viewed'],
  [/^\/learn\/trainer\/[^/]+\/?$/, 'Trainer Viewed'],
  [
    /^\/(?:learn\/trainer\/[^/]+\/lesson|learn\/catalog-lesson|trainings\/[^/]+\/lesson|arcades\/[^/]+\/lesson)\/[^/]+\/?$/,
    'Lesson Viewed',
  ],
  [/^\/reference\/?$/, 'Reference Viewed'],
  [/^\/rating\/?$/, 'Rating Viewed'],
  [/^\/trainings\/?$/, 'Trainings Viewed'],
  [/^\/trainings\/[^/]+\/?$/, 'Training Viewed'],
  [/^\/arcades\/?$/, 'Arcades Viewed'],
  [/^\/arcades\/[^/]+\/?$/, 'Arcade Viewed'],
  [/^\/profile\/?$/, 'Profile Viewed'],
  [/^\/profile\/[^/]+\/?$/, 'Student Profile Viewed'],
  [/^\/pricing\/?$/, 'Pricing Viewed'],
  [/^\/subscribe(?:\/|$)/, 'Subscribe Page Viewed'],
  [/^\/checkout(?:\/|$)/, 'Checkout Page Viewed'],
];

/** The view event whose screen this is (`App Opened` borrows its entry route's screen). */
const resolveScreenEventName = (
  eventName: string,
  properties: Record<string, unknown> | null | undefined,
): string => {
  const baseName = getAnalyticsEventBaseName(eventName);

  if (baseName !== 'App Opened') {
    return baseName;
  }

  const entryPath = readNonEmptyString(properties?.entry_path);
  const match = entryPath
    ? ENTRY_PATH_SCREEN_EVENTS.find(([pattern]) => pattern.test(entryPath))
    : null;

  return match ? match[1] : baseName;
};

const normalizeForComparison = (value: string) =>
  value.toLowerCase().replace(/ё/g, 'е');

export type CatalogKind = 'trainer' | 'training' | 'arcade' | 'course';

const CATALOG_KINDS: CatalogKind[] = [
  'trainer',
  'training',
  'arcade',
  'course',
];

const escapeRegExp = (value: string) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Kind words catalog titles start with. Titles are content (Russian) while the
 * admin panel may be in English, so both spellings count besides the UI label.
 */
const CATALOG_TITLE_KIND_WORDS: Record<CatalogKind, string[]> = {
  trainer: ['тренажёр', 'trainer'],
  training: ['тренинг', 'training'],
  arcade: ['аркада', 'arcade'],
  course: ['курс', 'course'],
};

const getCatalogKindWords = (kind: CatalogKind) => [
  ...new Set(
    [
      getTranslation(`${SCREENS_PREFIX}.${kind}`),
      ...CATALOG_TITLE_KIND_WORDS[kind],
    ].map(normalizeForComparison),
  ),
];

/** True when a catalog title starts with its kind word («Тренажёр. …», «Тренажер …»). */
const startsWithCatalogKind = (kind: CatalogKind, name: string) =>
  getCatalogKindWords(kind).some((word) =>
    normalizeForComparison(name).startsWith(word),
  );

/** «Тренажёр. …» / «Тренажер: …» / «Trainer — …»: length of the kind word and its separator. */
const matchCatalogKindPrefix = (kind: CatalogKind, name: string) => {
  const normalizedName = normalizeForComparison(name);

  for (const word of getCatalogKindWords(kind)) {
    const match = new RegExp(
      `^${escapeRegExp(word)}\\s*[.:—–-]\\s*(?=\\S)`,
    ).exec(normalizedName);

    if (match) {
      return match[0].length;
    }
  }

  return null;
};

/**
 * «Тренажёр. Auto Layout в Figma» → «Auto Layout в Figma»: inside a sentence
 * the kind is said by the noun before the name («тренажёр «Auto Layout в Figma»»).
 */
export const stripCatalogKindPrefix = (
  kind: CatalogKind,
  name: string,
): string => {
  const prefixLength = matchCatalogKindPrefix(kind, name);

  return prefixLength != null ? name.slice(prefixLength) : name;
};

/** Kind a raw catalog title announces with its prefix («Тренинг. …» → training). */
const detectCatalogKind = (name: string): CatalogKind | null =>
  CATALOG_KINDS.find((kind) => matchCatalogKindPrefix(kind, name) != null) ??
  null;

/**
 * «Тренажёр «Основы»», but a catalog title that already starts with its kind
 * («Тренажёр. Auto Layout в Figma») is shown as is — except inside a sentence,
 * where the prefix goes and the name gets the usual template, so the verdict,
 * the story and the summary name it the same way.
 */
const formatNamedScreen = (
  kind: CatalogKind,
  name: string | null,
  maxNameLength?: number,
  inSentence = false,
): string => {
  const kindLabel = getTranslation(`${SCREENS_PREFIX}.${kind}`);

  if (!name) {
    return kindLabel;
  }

  const strippedName = inSentence ? stripCatalogKindPrefix(kind, name) : name;

  if (strippedName === name && startsWithCatalogKind(kind, name)) {
    return truncateText(name, maxNameLength);
  }

  return getTranslation(`${SCREENS_PREFIX}.${kind}_named`, {
    name: truncateText(strippedName, maxNameLength),
  });
};

export type CatalogParentPhrases = {
  /** «тренинг «Дизайн мобильного приложения»» — for «Прошёл урок «X» (…)». */
  name: string;
  /** «в тренинге «Дизайн мобильного приложения»» — for «Прошёл урок …». */
  inPhrase: string;
};

/**
 * The trainer / training / arcade / course an event belongs to, named for a
 * sentence: the kind comes from the title prefix or the flow, the prefix goes.
 */
export const describeCatalogParentInSentence = (
  properties: Record<string, unknown> | null | undefined,
  maxNameLength?: number,
): CatalogParentPhrases | null => {
  const parentName = resolveCatalogParentLabel(properties ?? undefined);

  if (!parentName) {
    return null;
  }

  const flowKind = resolveModuleFlowKind(properties ?? undefined);
  const kind =
    detectCatalogKind(parentName) ??
    CATALOG_KINDS.find((candidate) => candidate === flowKind) ??
    null;

  if (!kind) {
    const name = truncateText(parentName, maxNameLength);

    return {
      name,
      inPhrase: getTranslation('student_page.journey.catalog_parent_in.other', {
        name,
      }),
    };
  }

  const name = truncateText(
    stripCatalogKindPrefix(kind, parentName),
    maxNameLength,
  );

  return {
    name: lowercaseFirst(
      getTranslation(`${SCREENS_PREFIX}.${kind}_named`, { name }),
    ),
    inPhrase: getTranslation(`student_page.journey.catalog_parent_in.${kind}`, {
      name,
    }),
  };
};

const readPositiveNumber = (value: unknown): number | null => {
  const number = readFiniteNumber(value);

  return number != null && number > 0 ? number : null;
};

/**
 * «Урок «X»»; an unnamed lesson says where it is: «Урок 3 из 14 в «Тренажёр…»»,
 * inside a sentence «Урок 3 из 14 в тренажёре «…»».
 */
const formatLessonScreen = (
  properties: Record<string, unknown> | null | undefined,
  maxNameLength?: number,
  inSentence = false,
): string => {
  const lessonName = readNonEmptyString(properties?.lesson_name);

  if (lessonName) {
    return getTranslation(`${SCREENS_PREFIX}.lesson_named`, {
      name: truncateText(lessonName, maxNameLength),
    });
  }

  const parentName = resolveCatalogParentLabel(properties ?? undefined);
  const parent = parentName ? truncateText(parentName, maxNameLength) : null;
  const lessonIndex = readPositiveNumber(properties?.lesson_index);
  const lessonsTotal = readPositiveNumber(properties?.lessons_total);
  const position =
    lessonIndex != null && lessonsTotal != null
      ? { number: lessonIndex, total: lessonsTotal }
      : null;
  const parentPhrase = inSentence
    ? describeCatalogParentInSentence(properties, maxNameLength)
    : null;

  if (parentPhrase) {
    return position
      ? getTranslation(`${SCREENS_PREFIX}.lesson_position_in_parent_phrase`, {
          ...position,
          parent: parentPhrase.inPhrase,
        })
      : getTranslation(`${SCREENS_PREFIX}.lesson_in_parent_phrase`, {
          parent: parentPhrase.inPhrase,
        });
  }

  if (position && parent) {
    return getTranslation(`${SCREENS_PREFIX}.lesson_position_in_parent`, {
      ...position,
      parent,
    });
  }

  if (parent) {
    return getTranslation(`${SCREENS_PREFIX}.lesson_in_parent`, { parent });
  }

  return position
    ? getTranslation(`${SCREENS_PREFIX}.lesson_position`, position)
    : getTranslation(`${SCREENS_PREFIX}.lesson`);
};

type ScreenLabelOptions = {
  /** Cut catalog names to this length (verdict titles); the log shows them in full. */
  maxNameLength?: number;
  /**
   * The label goes into a sentence (verdict title, story, summary): catalog titles
   * lose their kind prefix — «Тренажёр «Auto Layout в Figma»», not «Тренажёр. Auto…».
   * Chapter headers and catalog blocks keep the raw catalog title.
   */
  inSentence?: boolean;
};

const buildScreenLabel = (
  screenEventName: string,
  properties: Record<string, unknown> | null | undefined,
  { maxNameLength, inSentence = false }: ScreenLabelOptions,
): string => {
  const read = (key: string) => readNonEmptyString(properties?.[key]);
  const moduleName = read('module_name');

  switch (eventNameToTranslationKey(screenEventName)) {
    case 'lesson_viewed':
      return formatLessonScreen(properties, maxNameLength, inSentence);
    case 'trainer_viewed':
      return formatNamedScreen(
        'trainer',
        resolveCatalogParentLabel(properties ?? undefined),
        maxNameLength,
        inSentence,
      );
    case 'training_viewed':
      return formatNamedScreen(
        'training',
        read('training_name') ?? moduleName,
        maxNameLength,
        inSentence,
      );
    case 'arcade_viewed':
      return formatNamedScreen(
        'arcade',
        read('arcade_name') ?? moduleName,
        maxNameLength,
        inSentence,
      );
    case 'course_viewed':
      return formatNamedScreen(
        'course',
        read('course_name') ?? moduleName,
        maxNameLength,
        inSentence,
      );
    case 'auth_viewed':
    case 'login_screen_viewed':
      return getTranslation(`${SCREENS_PREFIX}.auth`);
    case 'oauth_viewed': {
      const methodKey = resolveAuthMethodFromProperties(
        properties ?? undefined,
      );
      const method = methodKey ? translateAuthMethod(methodKey) : null;

      return method
        ? getTranslation(`${SCREENS_PREFIX}.oauth_named`, { method })
        : getTranslation(`${SCREENS_PREFIX}.oauth`);
    }
    case 'student_profile_viewed': {
      const studentName = read('student_name');

      return studentName
        ? getTranslation(`${SCREENS_PREFIX}.student_profile_named`, {
            name: truncateText(studentName, maxNameLength),
          })
        : getTranslation(`${SCREENS_PREFIX}.student_profile`);
    }
    case 'plugin_intro_viewed':
      return getTranslation(`${SCREENS_PREFIX}.intro`);
    case 'welcome_viewed':
      return getTranslation(`${SCREENS_PREFIX}.welcome`);
    case 'pricing_viewed':
      return getTranslation(`${SCREENS_PREFIX}.pricing`);
    case 'streak_viewed':
      return getTranslation(`${SCREENS_PREFIX}.streak`);
    case 'paywall_viewed':
      return getTranslation(`${SCREENS_PREFIX}.paywall`);
    case 'app_opened':
      return getTranslation(`${SCREENS_PREFIX}.site_entry`);
    case 'plugin_opened':
      return getTranslation(`${SCREENS_PREFIX}.plugin_entry`);
    default:
      /* A new view event without copy is «Экран приложения», not «Действие в приложении». */
      return isKnownAnalyticsEvent(screenEventName)
        ? translateAnalyticsEvent(screenEventName)
        : getTranslation(`${SCREENS_PREFIX}.unknown`);
  }
};

/**
 * Human screen name from the screen dictionary: «Урок «Auto Layout»»,
 * «Тренажёр «Основы»», «Вход», «Вводная практика», «Раздел «Обучение»».
 */
export const getScreenLabel = (
  eventName: string | null | undefined,
  properties?: Record<string, unknown> | null,
  options: ScreenLabelOptions = {},
): string => {
  if (!eventName) {
    return getTranslation('student_page.journey.screen_start');
  }

  return buildScreenLabel(
    resolveScreenEventName(eventName, properties),
    properties,
    options,
  );
};

/** One word for narrow flow-bar segments: «Урок», «Тренажёр», «Вход». */
export const getScreenShortLabel = (
  eventName: string | null | undefined,
  properties?: Record<string, unknown> | null,
): string => {
  if (!eventName) {
    return getTranslation('student_page.journey.screen_start');
  }

  const screenEventName = resolveScreenEventName(eventName, properties);

  return (
    translateOptional(
      `student_page.journey.screen_short.${eventNameToTranslationKey(
        screenEventName,
      )}`,
    ) ??
    (isKnownAnalyticsEvent(screenEventName)
      ? buildScreenLabel(screenEventName, properties, {})
      : getTranslation('student_page.journey.screen_short.unknown'))
  );
};

export const getSessionExitScreenLabel = (
  session: Pick<SessionSummary, 'exitScreen'>,
) =>
  getScreenLabel(
    session.exitScreen?.eventName ?? null,
    session.exitScreen?.properties,
  );

export const getEventTitle = (
  event: Pick<SessionEvent, 'eventName' | 'properties'> &
    Partial<Pick<SessionEvent, 'platform'>>,
  studentEmail?: string | null,
) =>
  formatAnalyticsEventDisplay(event.eventName, event.properties, {
    studentEmail,
    platform: event.platform,
  }).title;

/* Session detail building blocks */

export type SessionEnvItem = {
  key: string;
  label: string;
  value: string;
};

export type SessionEnvDetails = {
  /** One line for everyone: «Figma для компьютера на macOS · 6 действий · сборка от 26 сент., 11:43». */
  summary: string;
  /** Raw values (versions, window size, screens) — «Технические детали» only. */
  techItems: SessionEnvItem[];
};

const ENV_PREFIX = 'student_page.journey.detail.env';

/** Figma `editorType` → product name (shown after the "Figma" label, not translated). */
const FIGMA_EDITOR_TYPE_LABELS: Record<string, string> = {
  figma: 'Design',
  figjam: 'FigJam',
  dev: 'Dev Mode',
  slides: 'Slides',
  buzz: 'Buzz',
};

/** Product name of a known Figma editor; `null` for unknown values (the raw one is tech-only). */
export const getFigmaEditorTypeLabel = (
  editorType: string | null | undefined,
): string | null => {
  const value = readNonEmptyString(editorType);

  return value ? FIGMA_EDITOR_TYPE_LABELS[value.toLowerCase()] ?? null : null;
};

/** `__APP_VERSION__` of both clients: `web-20260926.1143` / `plugin-20260926.1143`. */
const BUILD_VERSION_PATTERN =
  /^(?:web|plugin)-(\d{4})(\d{2})(\d{2})\.(\d{2})(\d{2})$/i;
/** Local and test builds report these instead of a stamp. */
const TEST_BUILD_VERSIONS = new Set(['dev', 'development', 'local', 'test']);

/** «сборка от 26 сент., 11:43» / «тестовая сборка»; `null` when the version has another format. */
export const formatBuildLabel = (
  appVersion: string | null | undefined,
  locale: string,
  now: Date = new Date(),
): string | null => {
  const version = readNonEmptyString(appVersion);

  if (!version) {
    return null;
  }

  if (TEST_BUILD_VERSIONS.has(version.toLowerCase())) {
    return getTranslation(`${ENV_PREFIX}.build_test`);
  }

  const match = BUILD_VERSION_PATTERN.exec(version);

  if (!match) {
    return null;
  }

  const [year, month, day, hours, minutes] = match.slice(1).map(Number);
  /* The stamp is shown as written (build machine time), so it is parsed and printed in UTC. */
  const buildDate = new Date(Date.UTC(year, month - 1, day, hours, minutes));

  if (
    buildDate.getUTCMonth() !== month - 1 ||
    buildDate.getUTCDate() !== day ||
    buildDate.getUTCHours() !== hours ||
    buildDate.getUTCMinutes() !== minutes
  ) {
    return null;
  }

  return getTranslation(`${ENV_PREFIX}.build_date`, {
    date: new Intl.DateTimeFormat(locale, {
      day: 'numeric',
      month: 'short',
      ...(year !== now.getFullYear() ? { year: 'numeric' as const } : {}),
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
      timeZone: 'UTC',
    }).format(buildDate),
  });
};

const readMajorVersion = (version: string | null | undefined) =>
  readNonEmptyString(version)?.split('.')[0] ?? null;

/** «Figma для компьютера на macOS», «Figma в браузере Chrome 129 на Windows», «Chrome 129 на Windows». */
const describeSessionDevice = (
  platform: ClientPlatform | undefined,
  env: SessionEnv | null,
): string | null => {
  const os = readNonEmptyString(env?.os);
  const browserName = readNonEmptyString(env?.browser);
  const browser = browserName
    ? joinNonEmpty([browserName, readMajorVersion(env?.browserVersion)], ' ')
    : null;

  if (platform === 'Plugin') {
    /* The Figma desktop app reports itself as the `Figma` browser. */
    if (browserName?.toLowerCase() === 'figma') {
      return os
        ? getTranslation(`${ENV_PREFIX}.figma_desktop`, { os })
        : getTranslation(`${ENV_PREFIX}.figma_desktop_no_os`);
    }

    if (browser) {
      return os
        ? getTranslation(`${ENV_PREFIX}.figma_browser`, { browser, os })
        : getTranslation(`${ENV_PREFIX}.figma_browser_no_os`, { browser });
    }

    return os ? getTranslation(`${ENV_PREFIX}.figma_on_os`, { os }) : null;
  }

  if (browser && os) {
    return getTranslation(`${ENV_PREFIX}.browser_on_os`, { browser, os });
  }

  return browser ?? os;
};

const VIEWPORT_PATTERN = /^(\d+)\s*[x×]\s*(\d+)$/i;

const isSmallWebWindow = (env: SessionEnv | null) => {
  const deviceType = readNonEmptyString(env?.deviceType);
  const match = VIEWPORT_PATTERN.exec(readNonEmptyString(env?.viewport) ?? '');

  return (
    Boolean(match) &&
    (!deviceType || deviceType === 'desktop') &&
    Number(match?.[1]) < SMALL_WEB_VIEWPORT_WIDTH
  );
};

/**
 * The session header: a human device line with the actions count and the build
 * date, plus labelled raw values for the «Технические детали» mode.
 */
export const getSessionEnvDetails = (
  session: Pick<SessionSummary, 'env' | 'actionsCount' | 'screensCount'> &
    Partial<Pick<SessionSummary, 'platform'>>,
  {
    screensCount,
    techMode = false,
    locale = 'ru',
    now = new Date(),
  }: {
    screensCount?: number;
    techMode?: boolean;
    locale?: string;
    now?: Date;
  } = {},
): SessionEnvDetails => {
  const env = session.env;
  const isWeb = session.platform === 'Web';
  const deviceType = readNonEmptyString(env?.deviceType);
  const editorType = readNonEmptyString(env?.figmaEditorType);
  const editorLabel = getFigmaEditorTypeLabel(editorType);
  const summary = joinNonEmpty([
    describeSessionDevice(session.platform, env),
    isWeb && deviceType
      ? translateOptional(`student_page.journey.device_types.${deviceType}`)
      : null,
    isWeb && isSmallWebWindow(env)
      ? getTranslation(`${ENV_PREFIX}.small_window`)
      : null,
    /* Design is the default editor; FigJam / Dev Mode are worth noticing. */
    session.platform === 'Plugin' && editorType?.toLowerCase() !== 'figma'
      ? editorLabel
      : null,
    getTranslation('student_page.journey.list.actions', {
      count: session.actionsCount ?? 0,
    }),
    formatBuildLabel(env?.appVersion, locale, now),
  ]);

  if (!techMode) {
    return { summary, techItems: [] };
  }

  const techRows: Array<[string, string | null | undefined]> = [
    [
      'browser',
      readNonEmptyString(env?.browser)
        ? joinNonEmpty([env?.browser, env?.browserVersion], ' ')
        : null,
    ],
    ['figma_editor', editorLabel ?? editorType],
    ['figma_api', readNonEmptyString(env?.figmaApiVersion)],
    ['viewport', readNonEmptyString(env?.viewport)?.replace(/x/i, '×')],
    ['screens', String(screensCount ?? session.screensCount ?? 0)],
    ['version', readNonEmptyString(env?.appVersion)],
  ];

  return {
    summary,
    techItems: techRows
      .filter((row): row is [string, string] => Boolean(row[1]))
      .map(([key, value]) => ({
        key,
        label: getTranslation(`${ENV_PREFIX}.label_${key}`),
        value,
      })),
  };
};

export type ScreenFlowTone = 'error' | 'slow' | 'goal' | 'normal';

export type ScreenFlowSegment = {
  index: number;
  /**
   * What the bar prints: the full name, one word for a narrow segment, or `null`
   * when even the word would be cut (the segment shows only its duration).
   */
  label: string | null;
  /** Full screen name for the tooltip / accessible name. */
  fullLabel: string;
  durationMs: number;
  grow: number;
  /** Narrowest the segment may get: wide enough for its short word. */
  minWidthPx: number;
  tone: ScreenFlowTone;
  isExit: boolean;
};

export const getScreenTone = (
  screen: Pick<SessionScreen, 'hasError' | 'hasSlow' | 'hasGoal'>,
): ScreenFlowTone => {
  if (screen.hasError) {
    return 'error';
  }

  if (screen.hasSlow) {
    return 'slow';
  }

  return screen.hasGoal ? 'goal' : 'normal';
};

/** Width a segment needs to print this word whole; `null` when that is past the cap. */
export const getFlowSegmentLabelWidth = (label: string): number | null => {
  const width = Math.max(
    label.length * FLOW_LABEL_CHAR_WIDTH_PX + FLOW_SEGMENT_PADDING_PX,
    FLOW_SEGMENT_MIN_WIDTH_PX,
  );

  return width <= FLOW_SEGMENT_LABEL_MAX_WIDTH_PX ? width : null;
};

/** Segment width ∝ time on screen, with a minimal share so short screens stay clickable. */
export const buildScreenFlowSegments = (
  screens: SessionScreen[],
  totalDurationMs: number,
): ScreenFlowSegment[] => {
  const total = Math.max(
    totalDurationMs,
    screens.reduce((sum, screen) => sum + Math.max(screen.durationMs, 0), 0),
    1,
  );

  return screens.map((screen, position) => {
    const grow = Math.max(
      Math.max(screen.durationMs, 0) / total,
      MIN_SCREEN_SEGMENT_SHARE,
    );
    const fullLabel = getScreenLabel(screen.eventName, screen.properties);
    const shortLabel =
      grow < SHORT_SCREEN_SEGMENT_SHARE
        ? getScreenShortLabel(screen.eventName, screen.properties)
        : null;
    const shortLabelWidth =
      shortLabel != null ? getFlowSegmentLabelWidth(shortLabel) : null;

    return {
      index: screen.index,
      label:
        shortLabel == null
          ? fullLabel
          : shortLabelWidth != null
          ? shortLabel
          : null,
      fullLabel,
      durationMs: Math.max(screen.durationMs, 0),
      grow,
      minWidthPx: shortLabelWidth ?? FLOW_SEGMENT_MIN_WIDTH_PX,
      tone: getScreenTone(screen),
      isExit: position === screens.length - 1,
    };
  });
};

/** Full segment text for the tooltip / accessible name (the bar itself truncates it). */
export const getScreenFlowSegmentTitle = (
  segment: Pick<ScreenFlowSegment, 'label' | 'durationMs' | 'isExit'> &
    Partial<Pick<ScreenFlowSegment, 'fullLabel'>>,
): string =>
  joinNonEmpty([
    segment.fullLabel ?? segment.label,
    formatDuration(segment.durationMs),
    segment.isExit
      ? getTranslation('student_page.journey.detail.flow_exit')
      : null,
  ]);

export type ScreenEventGroup = {
  screen: SessionScreen;
  label: string;
  offsetMs: number;
  events: SessionEvent[];
};

/** Splits the event log into screen groups; events with an unknown screen stay with the previous group. */
export const groupEventsByScreen = (
  detail: Pick<
    AnalyticsStudentSessionDetailDto,
    'screens' | 'events' | 'startedAt'
  >,
): ScreenEventGroup[] => {
  const sessionStart = getTimestamp(detail.startedAt);
  const screens: SessionScreen[] =
    detail.screens.length > 0
      ? detail.screens
      : [
          {
            index: 0,
            eventName: null,
            properties: {},
            startedAt: detail.startedAt,
            durationMs: 0,
            firstEventIndex: 0,
            lastEventIndex: Math.max(detail.events.length - 1, 0),
            hasError: false,
            hasSlow: false,
            hasGoal: false,
          },
        ];
  const groups = screens.map((screen) => ({
    screen,
    label: getScreenLabel(screen.eventName, screen.properties),
    offsetMs: Math.max(getTimestamp(screen.startedAt) - sessionStart, 0),
    events: [] as SessionEvent[],
  }));
  const groupPositionByScreenIndex = new Map(
    groups.map((group, position) => [group.screen.index, position]),
  );
  let currentPosition = 0;

  detail.events.forEach((event) => {
    const position = groupPositionByScreenIndex.get(event.screenIndex);

    if (position != null) {
      currentPosition = position;
    }

    groups[currentPosition].events.push(event);
  });

  return groups.filter(
    (group) => group.events.length > 0 || detail.screens.length > 0,
  );
};

export const shouldShowGapBefore = (
  event: Pick<SessionEvent, 'gapBeforeMs' | 'kind'>,
) => event.kind !== 'exit' && event.gapBeforeMs >= LOG_GAP_THRESHOLD_MS;

const readLessonIdentity = (
  properties: Record<string, unknown> | undefined,
): string | null =>
  readCatalogId(properties?.lesson_id) ??
  readNonEmptyString(properties?.lesson_name);

/**
 * `module_id ?? training_id ?? arcade_id`: a web training / arcade screen carries
 * `training_id` / `arcade_id` while its lesson events carry the same id as `module_id`.
 */
const readModuleCatalogIdentity = (
  properties: Record<string, unknown> | undefined,
): string | null =>
  readModuleIdentity(properties) ??
  resolveCatalogParentLabel(properties) ??
  readNonEmptyString(properties?.module_name);

/** Which lines of an event's catalog block its chapter header already says. */
export type CatalogContextRedundancy = {
  /** The whole block (and the names in the subtitle) repeats the header. */
  all: boolean;
  /** The course / trainer line («Курс. UI/UX-дизайн с нуля»). */
  course: boolean;
  /** The lesson name. */
  lesson: boolean;
};

const NO_CATALOG_REDUNDANCY: CatalogContextRedundancy = {
  all: false,
  course: false,
  lesson: false,
};

/**
 * What of an event's catalog block its chapter already shows (the screen's own
 * view event keeps everything). Lesson ids are unique, so the same lesson with no
 * module on one side is the whole block; the same lesson under another module id
 * (a course screen vs its lesson's course module) keeps only «Модуль: …». Under
 * the same trainer / course the parent line goes: a lesson row in a trainer
 * chapter keeps the lesson name, a module row in a lesson chapter keeps «Модуль: …».
 */
export const getEventCatalogRedundancy = (
  event: Pick<SessionEvent, 'index' | 'properties'>,
  screen: Pick<SessionScreen, 'eventName' | 'firstEventIndex' | 'properties'>,
): CatalogContextRedundancy => {
  if (!screen.eventName || event.index === screen.firstEventIndex) {
    return NO_CATALOG_REDUNDANCY;
  }

  const lesson = readLessonIdentity(event.properties);
  const module = readModuleCatalogIdentity(event.properties);
  const screenLesson = readLessonIdentity(screen.properties);
  const screenModule = readModuleCatalogIdentity(screen.properties);
  const parent = resolveCatalogParentLabel(event.properties);
  const sameParent =
    (Boolean(module) && module === screenModule) ||
    (parent != null &&
      (parent === resolveCatalogParentLabel(screen.properties) ||
        parent === readNonEmptyString(screen.properties?.module_name)));

  if (lesson && lesson === screenLesson) {
    return !module || !screenModule || module === screenModule
      ? { all: true, course: true, lesson: true }
      : { all: false, course: sameParent, lesson: true };
  }

  if (!lesson && Boolean(module) && !screenLesson && module === screenModule) {
    return { all: true, course: true, lesson: true };
  }

  /* «Прошёл модуль» in a lesson chapter of the same course keeps «Модуль: …» only. */
  return (!lesson || !screenLesson) && sameParent
    ? { all: false, course: true, lesson: false }
    : NO_CATALOG_REDUNDANCY;
};

/** True when an event only repeats the lesson/module its chapter header already shows. */
export const isEventCatalogContextRedundant = (
  event: Pick<SessionEvent, 'index' | 'properties'>,
  screen: Pick<SessionScreen, 'eventName' | 'firstEventIndex' | 'properties'>,
): boolean => getEventCatalogRedundancy(event, screen).all;

/* Error and slow details */

export type EventDetailRow = {
  key: string;
  label: string;
  value: string;
};

export type ErrorDiagnostics = {
  message: string | null;
  stack: string | null;
};

export type ErrorDetailsContent = {
  /** At most two human rows: «Что сломалось», «Ответ сервера». */
  summaryRows: EventDetailRow[];
  /** Request, response code, error name, request id, build — «Подробности для разработчика». */
  devRows: EventDetailRow[];
  diagnostics: ErrorDiagnostics;
};

const ERROR_DETAILS_PREFIX = 'student_page.journey.error_details';

const toDetailRows = (
  rows: Array<[string, string | null | undefined]>,
): EventDetailRow[] =>
  rows
    .filter((row): row is [string, string] => Boolean(row[1]))
    .map(([key, value]) => ({
      key,
      label: getTranslation(`${ERROR_DETAILS_PREFIX}.${key}`),
      value,
    }));

const isEventNamed = (eventName: string, baseName: string) =>
  getAnalyticsEventBaseName(eventName) === baseName;

/** API Error properties with the merged server error as a fallback for endpoint and status. */
const readApiErrorProperties = (
  event: Pick<SessionEvent, 'properties' | 'serverError'>,
): Record<string, unknown> => {
  const properties = event.properties ?? {};

  return {
    ...properties,
    endpoint:
      readNonEmptyString(properties.endpoint) ??
      readNonEmptyString(event.serverError?.endpoint),
    status:
      readFiniteNumber(properties.status) ??
      readFiniteNumber(event.serverError?.status),
  };
};

/**
 * The failed request as prose for the verdict: «При загрузке урока — ошибка на
 * сервере (500)», «Ошибка при проверке задания». Row titles keep the short form
 * («Не загрузилось: урок»); sentences never paste it in.
 */
export const describeApiFailure = (
  event: Pick<SessionEvent, 'properties' | 'serverError'>,
): string => {
  const properties = readApiErrorProperties(event);
  const when = getEndpointWhen(
    readNonEmptyString(properties.method),
    readNonEmptyString(properties.endpoint),
  );
  const status = getHttpStatusLabel(
    readFiniteNumber(properties.status),
    readNonEmptyString(properties.error_code),
  );

  return status
    ? getTranslation('student_page.journey.verdict.api_failure', {
        when: capitalizeFirst(when),
        status,
      })
    : getTranslation('student_page.journey.verdict.api_failure_no_status', {
        when,
      });
};

/** «Сбой внутри плагина Figma при загрузке урока» — the crash kind as one phrase. */
export const describeClientFailure = (
  properties: Record<string, unknown> = {},
): string => {
  const kind = getClientErrorKindLabel(properties);
  const description = getTranslation(
    `student_page.timeline.client_error_kinds.${kind}`,
  );
  const messageType = readNonEmptyString(properties.message_type);
  const step =
    kind === 'main_thread' && messageType
      ? translateOptional(
          `student_page.timeline.client_error_steps.${messageType.toUpperCase()}`,
        )
      : null;

  return step ? `${description} ${step}` : description;
};

/** «ошибка на сервере (500) через 1,2 с»; a 4xx adds «обычно не мешает студенту». */
const formatServerResponse = (
  properties: Record<string, unknown>,
): string | null => {
  const status = readFiniteNumber(properties.status);
  const errorCode = readNonEmptyString(properties.error_code);
  const statusLabel = getHttpStatusLabel(status, errorCode);

  if (!statusLabel) {
    return null;
  }

  const kind = getHttpStatusKind(status, errorCode);
  const durationMs = readFiniteNumber(properties.duration_ms);
  const response =
    durationMs != null
      ? getTranslation(
          `${ERROR_DETAILS_PREFIX}.${
            kind === 'network' || kind === 'timeout'
              ? 'response_waited'
              : 'response_after'
          }`,
          { status: statusLabel, duration: formatPreciseSeconds(durationMs) },
        )
      : statusLabel;

  return kind === 'rejected'
    ? getTranslation(`${ERROR_DETAILS_PREFIX}.response_harmless`, { response })
    : response;
};

/** Long texts for developers: server/client message and stack. */
export const getErrorDiagnostics = (
  event: Pick<SessionEvent, 'properties' | 'serverError'>,
): ErrorDiagnostics => {
  const properties = event.properties ?? {};

  return {
    message:
      readNonEmptyString(event.serverError?.message) ??
      readNonEmptyString(properties.server_message) ??
      readNonEmptyString(properties.message),
    stack:
      readNonEmptyString(event.serverError?.stack) ??
      readNonEmptyString(properties.stack) ??
      readNonEmptyString(properties.component_stack),
  };
};

/** Request ids of an error row and of the identical ones folded into it («×3»). */
const collectRequestIds = (
  events: Array<Pick<SessionEvent, 'properties'>>,
): string | null =>
  joinNonEmpty(
    [
      ...new Set(
        events.map((event) => readNonEmptyString(event.properties?.request_id)),
      ),
    ],
    ', ',
  ) || null;

/**
 * The error card split in two: human rows everyone sees and the developer rows
 * behind «Подробности для разработчика». A «Что сломалось» row that only repeats
 * the row title («Не загрузилось: урок») is dropped. `foldedEvents` — identical
 * errors folded into the row: their request ids join the developer rows.
 */
export const getErrorDetails = (
  event: Pick<SessionEvent, 'eventName' | 'properties' | 'serverError'>,
  {
    appVersion,
    title,
    foldedEvents = [],
  }: {
    appVersion?: string | null;
    title?: string | null;
    foldedEvents?: Array<Pick<SessionEvent, 'properties'>>;
  } = {},
): ErrorDetailsContent => {
  const properties = event.properties ?? {};
  const apiProperties = readApiErrorProperties(event);
  const endpoint = readNonEmptyString(apiProperties.endpoint);
  const status = readFiniteNumber(apiProperties.status);
  const durationMs = readFiniteNumber(properties.duration_ms);
  const isApiError = isEventNamed(event.eventName, 'API Error') || !!endpoint;
  let what: string | null = null;

  if (isApiError && endpoint) {
    what = lowercaseFirst(getApiErrorTitle(apiProperties));
  } else if (isEventNamed(event.eventName, 'Client Error')) {
    what = describeClientFailure(properties);
  }

  const repeatsTitle =
    what != null &&
    Boolean(title) &&
    String(title).toLowerCase().includes(what.toLowerCase());

  return {
    summaryRows: toDetailRows([
      ['what', repeatsTitle ? null : what],
      ['response', isApiError ? formatServerResponse(apiProperties) : null],
    ]),
    devRows: toDetailRows([
      [
        'request',
        endpoint
          ? joinNonEmpty([readNonEmptyString(properties.method), endpoint], ' ')
          : null,
      ],
      [
        'status',
        status != null
          ? String(status)
          : readNonEmptyString(properties.error_code),
      ],
      [
        'duration',
        durationMs != null ? formatPreciseSeconds(durationMs) : null,
      ],
      [
        'error_name',
        readNonEmptyString(properties.error_name) ??
          readNonEmptyString(event.serverError?.errorName),
      ],
      ['error_kind', readNonEmptyString(properties.error_kind)],
      ['request_id', collectRequestIds([event, ...foldedEvents])],
      ['app_version', readNonEmptyString(appVersion)],
    ]),
    diagnostics: getErrorDiagnostics(event),
  };
};

/** True when the card has something beyond the build line. */
export const hasErrorDetailsCard = (content: ErrorDetailsContent): boolean =>
  content.summaryRows.length > 0 ||
  content.devRows.some((row) => row.key !== 'app_version') ||
  Boolean(content.diagnostics.message || content.diagnostics.stack);

/**
 * Plain text for «Скопировать для разработчика»: when, who, where and every raw
 * value of the error in one paste-able block.
 */
export const buildDeveloperReport = ({
  event,
  title,
  studentId,
  appVersion,
  foldedEvents = [],
}: {
  event: Pick<
    SessionEvent,
    'eventName' | 'properties' | 'serverError' | 'timestamp' | 'platform'
  >;
  title?: string | null;
  studentId?: string | null;
  appVersion?: string | null;
  /** Identical errors folded into the row («×3»): the report counts them and lists their times. */
  foldedEvents?: Array<Pick<SessionEvent, 'properties' | 'timestamp'>>;
}): string => {
  const { summaryRows, devRows, diagnostics } = getErrorDetails(event, {
    appVersion,
    foldedEvents,
  });
  const label = (key: string) =>
    getTranslation(`${ERROR_DETAILS_PREFIX}.${key}`);
  const lines = [
    [label('event'), joinNonEmpty([title, `(${event.eventName})`], ' ')],
    [
      label('time'),
      joinNonEmpty(
        [event, ...foldedEvents].map((item) =>
          readNonEmptyString(item.timestamp),
        ),
        ', ',
      ),
    ],
    [
      label('repeats'),
      foldedEvents.length > 0 ? String(foldedEvents.length + 1) : null,
    ],
    [label('student'), readNonEmptyString(studentId)],
    [label('platform'), readNonEmptyString(event.platform)],
    ...[...summaryRows, ...devRows].map((row) => [row.label, row.value]),
    [label('message'), diagnostics.message],
  ]
    .filter((line): line is [string, string] => Boolean(line[1]))
    .map(([name, value]) => `${name}: ${value}`);

  return diagnostics.stack
    ? [...lines, `${label('stack')}:`, diagnostics.stack].join('\n')
    : lines.join('\n');
};

export type SlowEventDetails = {
  /** «Сервер отвечал 5,4 с», «Открывался 12,4 с», «Ждал 14,3 с». */
  durationLabel: string;
  /** A Slow Screen that never finished loading (`completed: false`). */
  notCompleted: boolean;
  /** `GET /api/v1/…` or the raw stage — «Технические детали» only. */
  devTarget: string | null;
  durationMs: number;
  thresholdMs: number;
  fillPercent: number;
  thresholdPercent: number;
};

/** Duration bar data for Slow Request / Slow Screen (threshold marker on the same scale). */
export const getSlowEventDetails = (
  event: Pick<SessionEvent, 'eventName' | 'properties'>,
): SlowEventDetails | null => {
  const properties = event.properties ?? {};
  const durationMs = readFiniteNumber(properties.duration_ms);

  if (durationMs == null) {
    return null;
  }

  const isSlowScreen = isEventNamed(event.eventName, 'Slow Screen');
  const notCompleted = isSlowScreen && properties.completed === false;
  const thresholdMs =
    readFiniteNumber(properties.threshold_ms) ??
    (isSlowScreen
      ? DEFAULT_SLOW_SCREEN_THRESHOLD_MS
      : DEFAULT_SLOW_REQUEST_THRESHOLD_MS);
  const scaleMs = Math.max(durationMs, thresholdMs, 1) * SLOW_SCALE_HEADROOM;
  const endpoint = readNonEmptyString(properties.endpoint);
  const durationKey = isSlowScreen
    ? notCompleted
      ? 'screen_waited'
      : 'screen_duration'
    : 'request_duration';

  return {
    durationLabel: getTranslation(
      `student_page.journey.slow_details.${durationKey}`,
      { duration: formatPreciseSeconds(durationMs) },
    ),
    notCompleted,
    devTarget: isSlowScreen
      ? readNonEmptyString(properties.stage)
      : endpoint
      ? joinNonEmpty([readNonEmptyString(properties.method), endpoint], ' ')
      : null,
    durationMs,
    thresholdMs,
    fillPercent: Math.min((durationMs / scaleMs) * 100, 100),
    thresholdPercent: Math.min((thresholdMs / scaleMs) * 100, 100),
  };
};

/**
 * True when the row's card already says what its subtitle would, so the log
 * drops the duplicate line: slow cards show the timing (and «так и не открылся»),
 * the error card shows the server response or what crashed.
 */
export const isEventSubtitleCoveredByDetails = (
  event: Pick<SessionEvent, 'eventName'>,
  summaryRows: Array<Pick<EventDetailRow, 'key'>>,
  slowDetails: SlowEventDetails | null,
): boolean => {
  const baseName = getAnalyticsEventBaseName(event.eventName);

  if (baseName === 'Slow Request' || baseName === 'Slow Screen') {
    return slowDetails != null;
  }

  if (baseName === 'API Error') {
    return summaryRows.some((row) => row.key === 'response');
  }

  return (
    baseName === 'Client Error' && summaryRows.some((row) => row.key === 'what')
  );
};

/* Verdict */

export type SessionVerdictContent = {
  cause: ExitCause;
  /** The screen name is cut to SCREEN_NAME_MAX_LENGTH… */
  title: string;
  /** …and here in full, for the title's tooltip. */
  fullTitle: string;
  text: string;
  notes: string[];
  /** Where «К ошибке ↓» jumps: the root cause when one was found. */
  causeEventIndex: number | null;
  jumpLabel: string | null;
};

const VERDICT_PREFIX = 'student_page.journey.verdict';

/** Student actions worth quoting as «последнее действие» (closing and signing out are the exit itself). */
const LAST_ACTION_KINDS = new Set<EventKind>(['action', 'goal']);
const EXIT_LIKE_EVENT_NAMES = new Set([
  'Plugin Closed',
  'Session Ended',
  'Profile Logged Out',
]);

const findEventByIndex = (
  detail: Pick<AnalyticsStudentSessionDetailDto, 'events'>,
  eventIndex: number | null | undefined,
): SessionEvent | null =>
  eventIndex != null
    ? detail.events.find((event) => event.index === eventIndex) ?? null
    : null;

/**
 * The exit screen for the verdict title, as a sentence part («урок «X»»,
 * «тренажёр «Auto Layout в Figma»»): cut to fit the title, and in full for its tooltip.
 */
const resolveVerdictScreenLabel = (
  detail: AnalyticsStudentSessionDetailDto,
  causeEvent: SessionEvent | null,
): { label: string; fullLabel: string } => {
  const screenIndex =
    detail.exit.screenIndex ?? causeEvent?.screenIndex ?? null;
  const screen =
    (screenIndex != null
      ? detail.screens.find((candidate) => candidate.index === screenIndex)
      : null) ?? detail.screens[detail.screens.length - 1];
  const source = screen ?? detail.exitScreen;
  const build = (maxNameLength?: number) =>
    lowercaseFirst(
      getScreenLabel(source?.eventName ?? null, source?.properties, {
        maxNameLength,
        inSentence: true,
      }),
    );

  return { label: build(SCREEN_NAME_MAX_LENGTH), fullLabel: build() };
};

/**
 * A failed request earlier on the same screen: the later crash most likely came
 * from it, so the verdict names it as the root cause.
 */
export const findErrorRootCause = (
  events: SessionEvent[],
  causeEvent: SessionEvent,
): SessionEvent | null => {
  if (
    causeEvent.kind !== 'error' ||
    isEventNamed(causeEvent.eventName, 'API Error')
  ) {
    return null;
  }

  for (let position = events.length - 1; position >= 0; position -= 1) {
    const candidate = events[position];

    if (
      candidate.index < causeEvent.index &&
      candidate.screenIndex === causeEvent.screenIndex &&
      candidate.kind === 'error' &&
      isEventNamed(candidate.eventName, 'API Error')
    ) {
      return candidate;
    }
  }

  return null;
};

/** Row subtitle parts inside a sentence: «тариф «Месяц», 990 ₽», not «Тариф «Месяц» · 990 ₽». */
const toSentenceDetails = (subtitle: string) =>
  lowercaseFirst(subtitle.split(' · ').join(', '));

const describeErrorEvent = (
  event: SessionEvent,
  studentEmail?: string | null,
): string => {
  if (isEventNamed(event.eventName, 'API Error')) {
    return describeApiFailure(event);
  }

  if (isEventNamed(event.eventName, 'Client Error')) {
    return describeClientFailure(event.properties);
  }

  const display = formatAnalyticsEventDisplay(
    event.eventName,
    event.properties,
    { studentEmail, platform: event.platform },
  );

  return display.subtitle
    ? getTranslation(`${VERDICT_PREFIX}.event_failure`, {
        event: display.title,
        details: toSentenceDetails(display.subtitle),
      })
    : display.title;
};

/** What happened right after: «студент перезапустил плагин», «сессия закончилась». */
const getVerdictExitAction = (
  detail: Pick<
    AnalyticsStudentSessionDetailDto,
    'endReason' | 'followedByReload' | 'platform'
  >,
): string => {
  let action = 'ended';

  if (
    detail.endReason === 'restart' ||
    (detail.followedByReload && detail.platform === 'Plugin')
  ) {
    action = 'restart';
  } else if (detail.followedByReload) {
    action = 'reload';
  } else if (detail.endReason === 'logout') {
    action = 'logout';
  } else if (detail.endReason === 'closed') {
    action = detail.platform === 'Plugin' ? 'closed_plugin' : 'closed_web';
  }

  return getTranslation(`${VERDICT_PREFIX}.exit_actions.${action}`);
};

const getErrorVerdictText = (
  detail: AnalyticsStudentSessionDetailDto,
  causeEvent: SessionEvent | null,
  rootCause: SessionEvent | null,
  studentEmail?: string | null,
) => {
  if (!causeEvent) {
    return getTranslation(`${VERDICT_PREFIX}.error_text_generic`);
  }

  /* After a root cause the crash is a short clause: «…, после этого экран сломался». */
  const what = rootCause
    ? getTranslation(`${VERDICT_PREFIX}.root_then`, {
        root: describeApiFailure(rootCause),
        then: isEventNamed(causeEvent.eventName, 'Client Error')
          ? describeClientErrorBriefly(causeEvent.properties)
          : lowercaseFirst(describeErrorEvent(causeEvent, studentEmail)),
      })
    : describeErrorEvent(causeEvent, studentEmail);
  const action = getVerdictExitAction(detail);

  return detail.exit.sinceCauseMs != null
    ? getTranslation(`${VERDICT_PREFIX}.error_text`, {
        what,
        since: formatDuration(detail.exit.sinceCauseMs),
        action,
      })
    : getTranslation(`${VERDICT_PREFIX}.error_text_no_since`, {
        what,
        action,
      });
};

const getStuckVerdictText = (
  causeEvent: SessionEvent | null,
  studentEmail?: string | null,
) => {
  if (!causeEvent) {
    return getTranslation(`${VERDICT_PREFIX}.stuck_text_no_event`);
  }

  /* «Долго ждал ответа сервера при загрузке урока», not the row title «Долго ждал: урок». */
  const what = isEventNamed(causeEvent.eventName, 'Slow Request')
    ? getTranslation(`${VERDICT_PREFIX}.slow_request_what`, {
        when: getEndpointWhen(
          readNonEmptyString(causeEvent.properties?.method),
          readNonEmptyString(causeEvent.properties?.endpoint),
        ),
      })
    : getEventTitle(causeEvent, studentEmail);

  if (isEventNamed(causeEvent.eventName, 'Rage Click')) {
    return getTranslation(`${VERDICT_PREFIX}.stuck_text_rage`, { what });
  }

  const slowDetails = getSlowEventDetails(causeEvent);

  if (!slowDetails) {
    return getTranslation(`${VERDICT_PREFIX}.stuck_text_generic`, { what });
  }

  return getTranslation(
    `${VERDICT_PREFIX}.${
      slowDetails.notCompleted ? 'stuck_text_not_completed' : 'stuck_text'
    }`,
    {
      what,
      duration: formatPreciseSeconds(slowDetails.durationMs),
      threshold: formatPreciseSeconds(slowDetails.thresholdMs),
    },
  );
};

const LESSON_GOAL_EVENT_KEYS = new Set([
  'lesson_completed',
  'task_completed',
  'activation_completed',
]);

/**
 * «Прошёл урок «Автолейаут: основы» (Тренажёр. Автолейаут)», «Решил задачу 3 из 5
 * в уроке «X»», «Оплата прошла — тариф «Месяц» · 990 ₽». Lesson and module results
 * use catalog names only, never the «название не найдено» fallbacks of subtitles.
 */
const describeGoalResult = (
  event: SessionEvent,
  studentEmail?: string | null,
): string => {
  const display = formatAnalyticsEventDisplay(
    event.eventName,
    event.properties,
    { studentEmail, platform: event.platform },
  );
  const translationKey = eventNameToTranslationKey(
    getAnalyticsEventBaseName(event.eventName),
  );
  const properties = event.properties ?? {};
  const lessonName = readNonEmptyString(properties.lesson_name);
  const parentName = resolveCatalogParentLabel(properties);
  /* «тренинг «Дизайн…»» / «в тренинге «Дизайн…»», as the story and the verdict title say it. */
  const parent = describeCatalogParentInSentence(properties);
  const withParent = (result: string, name: string) =>
    parent && parentName !== name
      ? getTranslation(`${VERDICT_PREFIX}.goal_result_parent`, {
          result,
          parent: parent.name,
        })
      : result;

  if (LESSON_GOAL_EVENT_KEYS.has(translationKey)) {
    if (lessonName && translationKey === 'lesson_completed') {
      return withParent(
        getTranslation(`${VERDICT_PREFIX}.goal_result_named`, {
          event: display.title,
          name: lessonName,
        }),
        lessonName,
      );
    }

    if (lessonName) {
      return getTranslation(`${VERDICT_PREFIX}.goal_result_in_lesson`, {
        event: display.title,
        name: lessonName,
      });
    }

    return parent
      ? getTranslation(`${VERDICT_PREFIX}.goal_result_in_parent`, {
          event: display.title,
          parent: parent.inPhrase,
        })
      : display.title;
  }

  const moduleName = readNonEmptyString(properties.module_name) ?? parentName;

  if (translationKey === 'module_completed') {
    return moduleName
      ? withParent(
          getTranslation(`${VERDICT_PREFIX}.goal_result_named`, {
            event: display.title,
            name: moduleName,
          }),
          moduleName,
        )
      : display.title;
  }

  return display.subtitle
    ? getTranslation(`${VERDICT_PREFIX}.goal_result_details`, {
        event: display.title,
        details: toSentenceDetails(display.subtitle),
      })
    : display.title;
};

/** Title of the last thing the student did before leaving (not the exit itself). */
export const getLastStudentActionTitle = (
  detail: Pick<AnalyticsStudentSessionDetailDto, 'events'>,
  studentEmail?: string | null,
): string | null => {
  for (let position = detail.events.length - 1; position >= 0; position -= 1) {
    const event = detail.events[position];

    if (
      LAST_ACTION_KINDS.has(event.kind) &&
      !EXIT_LIKE_EVENT_NAMES.has(getAnalyticsEventBaseName(event.eventName))
    ) {
      return getEventTitle(event, studentEmail);
    }
  }

  return null;
};

const getVerdictText = (
  cause: ExitCause,
  detail: AnalyticsStudentSessionDetailDto,
  causeEvent: SessionEvent | null,
  rootCause: SessionEvent | null,
  studentEmail?: string | null,
) => {
  if (cause === 'error') {
    return getErrorVerdictText(detail, causeEvent, rootCause, studentEmail);
  }

  if (cause === 'stuck') {
    return getStuckVerdictText(causeEvent, studentEmail);
  }

  if (cause === 'goal') {
    return causeEvent
      ? getTranslation(`${VERDICT_PREFIX}.goal_text`, {
          result: describeGoalResult(causeEvent, studentEmail),
        })
      : getTranslation(`${VERDICT_PREFIX}.goal_text_generic`);
  }

  const lastAction = getLastStudentActionTitle(detail, studentEmail);

  if (cause === 'idle') {
    const active = formatDuration(detail.durationMs);

    return lastAction
      ? getTranslation(`${VERDICT_PREFIX}.idle_text`, {
          active,
          action: lastAction,
        })
      : getTranslation(`${VERDICT_PREFIX}.idle_text_no_action`, { active });
  }

  return lastAction
    ? getTranslation(`${VERDICT_PREFIX}.left_text`, { action: lastAction })
    : getTranslation(`${VERDICT_PREFIX}.left_text_generic`);
};

const getVerdictNotes = (
  cause: ExitCause,
  detail: AnalyticsStudentSessionDetailDto,
): string[] => {
  /* The error text already says what the student did next (restart, reload, sign-out). */
  if (cause === 'error') {
    return [];
  }

  const notes: string[] = [];

  if (detail.followedByReload) {
    // Web reloads the page; the plugin is restarted (admin-api also sets the flag for plugin restarts).
    notes.push(
      getTranslation(
        detail.platform === 'Plugin'
          ? `${VERDICT_PREFIX}.after_restart`
          : `${VERDICT_PREFIX}.after_reload`,
      ),
    );
  }

  if (detail.endReason === 'logout') {
    notes.push(getTranslation(`${VERDICT_PREFIX}.logout`));
  }

  if (cause === 'idle') {
    notes.push(getTranslation(`${VERDICT_PREFIX}.idle_hint`));
  }

  return notes;
};

/**
 * Why the session ended, in two sentences: the root cause of an error (a failed
 * request before a crash on the same screen), the named result of a goal, the
 * last action before idling.
 */
export const getSessionVerdict = (
  detail: AnalyticsStudentSessionDetailDto,
  studentEmail?: string | null,
): SessionVerdictContent => {
  const cause = detail.exit.cause ?? detail.exitCause;
  const causeEvent = findEventByIndex(detail, detail.exit.causeEventIndex);
  const rootCause =
    cause === 'error' && causeEvent
      ? findErrorRootCause(detail.events, causeEvent)
      : null;
  const jumpEvent = rootCause ?? causeEvent;
  const canJump = jumpEvent != null && (cause === 'error' || cause === 'stuck');
  const screen = resolveVerdictScreenLabel(detail, causeEvent);

  return {
    cause,
    title: getTranslation(`${VERDICT_PREFIX}.${cause}_title`, {
      screen: screen.label,
    }),
    fullTitle: getTranslation(`${VERDICT_PREFIX}.${cause}_title`, {
      screen: screen.fullLabel,
    }),
    text: getVerdictText(cause, detail, causeEvent, rootCause, studentEmail),
    notes: getVerdictNotes(cause, detail),
    causeEventIndex: jumpEvent ? jumpEvent.index : null,
    jumpLabel: canJump
      ? getTranslation(`${VERDICT_PREFIX}.jump_${cause}`)
      : null,
  };
};

/* Keyboard navigation */

export type JourneyNavigationDirection = 'next' | 'previous';

const NEXT_SESSION_KEYS = new Set(['j', 'J', 'о', 'О']);
const PREVIOUS_SESSION_KEYS = new Set(['k', 'K', 'л', 'Л']);

export const getJourneyNavigationDirection = (
  key: string,
): JourneyNavigationDirection | null => {
  if (NEXT_SESSION_KEYS.has(key)) {
    return 'next';
  }

  return PREVIOUS_SESSION_KEYS.has(key) ? 'previous' : null;
};

export const getAdjacentSessionId = (
  sessions: Array<Pick<SessionSummary, 'id'>>,
  currentSessionId: string | null,
  direction: JourneyNavigationDirection,
): string | null => {
  if (sessions.length === 0) {
    return null;
  }

  const currentPosition = sessions.findIndex(
    (session) => session.id === currentSessionId,
  );

  if (currentPosition === -1) {
    return sessions[0].id;
  }

  const nextPosition =
    direction === 'next'
      ? Math.min(currentPosition + 1, sessions.length - 1)
      : Math.max(currentPosition - 1, 0);

  return sessions[nextPosition].id;
};

const NON_TEXT_INPUT_TYPES = new Set([
  'checkbox',
  'radio',
  'button',
  'submit',
  'reset',
  'range',
]);

/** True when keyboard shortcuts must not fire (typing in a field). */
export const isEditableEventTarget = (target: unknown): boolean => {
  const element = target as {
    tagName?: string;
    type?: string;
    isContentEditable?: boolean;
  } | null;

  if (!element || typeof element.tagName !== 'string') {
    return false;
  }

  const tagName = element.tagName.toUpperCase();

  if (
    tagName === 'TEXTAREA' ||
    tagName === 'SELECT' ||
    element.isContentEditable
  ) {
    return true;
  }

  return (
    tagName === 'INPUT' &&
    !NON_TEXT_INPUT_TYPES.has(String(element.type).toLowerCase())
  );
};

/* ------------------------------------------------------------------------ */
/* Building blocks for the event log and the session story                  */
/* (journeyEventLog.ts, journeySessionStory.ts): the same readers and       */
/* wording as the verdict, so the log, the story and the verdict agree.     */
/* ------------------------------------------------------------------------ */

export {
  capitalizeFirst as capitalizeJourneyText,
  joinNonEmpty as joinJourneyParts,
  lowercaseFirst as lowercaseJourneyText,
  readCatalogId as readJourneyCatalogId,
  readLessonIdentity as readJourneyLessonIdentity,
  readModuleCatalogIdentity as readJourneyModuleIdentity,
  truncateText as truncateJourneyText,
};
